// src/components/bot/useChatController.js
// ─────────────────────────────────────────────────────────────────────────────
// Layer 2 — Chat Controller Hook
//
// Responsibilities:
//   • Own the messages[] display array and loading state (React state).
//   • Trigger initialization (AI settings + catalog warm-up) on mount.
//   • Expose send(text) which orchestrates layers 3–7:
//       1. Append user message to display + history
//       2. Call AI Planner → decide if backend data is needed
//       3. If yes → call API Executor → get backend data
//       4. Call Context Builder → serialize data for Gemini
//       5. Call Gemini Service → generate final answer
//       6. Append answer to display + history
//   • Handle all errors gracefully — never lets an unhandled rejection surface.
//
// InternalBotV2.jsx imports ONLY this hook and calls send(text).
// All orchestration logic lives here, not in the component.
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useCallback, useEffect } from 'react';
import { ensureSettings, generateAnswer }   from './geminiService';
import { warmUp, getFlat }                  from './catalogCache';
import { plan }                             from './aiPlanner';
import { execute }                          from './apiExecutor';
import { build as buildContext }            from './contextBuilder';

const DEBUG_AI = true;
import {
  toGeminiContents,
  appendUser,
  appendModel,
  reset as resetHistory,
} from './conversationManager';

// ── Constants ─────────────────────────────────────────────────────────────────

const WELCOME_MESSAGE = {
  id:   'welcome',
  role: 'bot',
  text: 'Hi! I\'m your VAHD AI assistant. Ask me anything about attendance, employees, cases, vaccinations, drugs, reports, and more — or just have a conversation.',
};

const MSG_INIT_ERROR  = 'AI assistant is temporarily unavailable. Please refresh the page and try again.';
const MSG_GEMINI_FAIL = 'I\'m having trouble connecting right now. Please try again in a moment.';
const MSG_GENERIC_ERR = 'Something went wrong. Please try again.';

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * useChatController — the main orchestrator hook.
 *
 * @returns {{
 *   messages:  Array<{ id: string|number, role: 'user'|'bot', text: string, loading?: boolean }>,
 *   loading:   boolean,
 *   send:      (text: string) => void,
 *   clearChat: () => void
 * }}
 */
export function useChatController() {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [loading,  setLoading]  = useState(false);

  // ── Initialization ─────────────────────────────────────────────────────────
  // Runs once on mount. Loads the Gemini API key/prompt and warms the catalog.
  // Both calls use module-level caches — subsequent open/close cycles are instant.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Run in parallel — neither depends on the other
        await Promise.all([ensureSettings(), warmUp()]);
        // Nothing to set here — services are now primed
      } catch (e) {
        if (cancelled) return;
        console.error('[ChatController] Initialization failed:', e.message);
        setMessages(prev => [
          ...prev,
          { id: 'init-error', role: 'bot', text: MSG_INIT_ERROR },
        ]);
      }
    })();

    return () => { cancelled = true; };
  }, []); // intentionally empty — runs once per component mount

  // ── Send ───────────────────────────────────────────────────────────────────
  /**
   * Process a user message through the full AI pipeline.
   *
   * Flow:
   *   UI input → appendUser → plan → (execute → buildContext)? → generateAnswer → appendModel → display
   *
   * The `answer` variable is initialized to a fallback string.
   * The `finally` block ALWAYS appends it to both the history and the display,
   * ensuring conversation history remains balanced regardless of which layer failed.
   */
  const send = useCallback(async (q) => {
    if (!q || !q.trim() || loading) return;
    setLoading(true);

    const startTime = Date.now();

    // Append user message immediately to give instant UI feedback
    setMessages(prev => [
      ...prev,
      { id: Date.now(), role: 'user', text: q },
    ]);

    // Record in conversation history BEFORE building the Gemini history snapshot
    appendUser(q);

    // Default answer — overwritten by Gemini or specific fallbacks below
    let answer = MSG_GENERIC_ERR;
    let msgOptions = null;

    try {
      // ── Step 1: Plan ───────────────────────────────────────────────────────
      // Ask Gemini (fast model) whether this question requires backend data.
      // Returns { needsApi, endpoint, params, answer }. Never throws.
      const geminiHistory = toGeminiContents();
      const planResult = await plan(q, geminiHistory);

      if (!planResult.needsApi) {
        if (planResult.needsOptions) {
          const loc = planResult.location || q.trim();
          answer = `Search results for "${loc}":`;
          msgOptions = [
            `Today Attendance in ${loc}`,
            `List Of Employees in ${loc}`,
            `Cases Treated in ${loc}`,
            `AI Services in ${loc}`,
            `Vaccinations in ${loc}`,
            `Deworming in ${loc}`,
          ];
        } else if (planResult.answer) {
          // Early return: no backend API needed, planner generated the response
          answer = planResult.answer;
        } else {
          answer = "I couldn't generate a response. Please rephrase your question.";
        }
      } else {
        // ── Step 2: Execute backend API (conditional) ──────────────────────────
        let contextBlock = null;

        if (planResult.endpoint) {
          if (DEBUG_AI) console.debug(`[Backend] API Called: ${planResult.endpoint}`);
          const apiResult = await execute(planResult.endpoint, planResult.params);

          if (apiResult.ok && apiResult.data) {
            // Find the human-readable name for this endpoint from the flat catalog
            const catalogEntry = getFlat().find(e => e.endpoint === planResult.endpoint);
            const endpointLabel = catalogEntry?.name || planResult.endpoint;

            // ── Step 3: Build context block ────────────────────────────────────
            contextBlock = buildContext(apiResult.data, endpointLabel);
          } else {
            // API call failed — tell Gemini so it can acknowledge it gracefully
            contextBlock = [
              'Note: The system attempted to retrieve live data but could not.',
              `Reason: ${apiResult.error || 'Unknown error'}.`,
              'Please answer based on your general knowledge about the VAHD system.',
            ].join(' ');
          }
        }

        // ── Step 5: Generate final answer ──────────────────────────────────────
        try {
          answer = await generateAnswer(geminiHistory, q, contextBlock);
        } catch (e) {
          console.error('[ChatController] Gemini generateAnswer failed:', e.message);
          answer = MSG_GEMINI_FAIL;
        }
      }



    } catch (e) {
      // Catches any unexpected error from plan() or execute()
      console.error('[ChatController] Pipeline error:', e.message);
      // answer remains as MSG_GENERIC_ERR
    } finally {
      const executionTime = Date.now() - startTime;
      if (DEBUG_AI) console.debug(`[ChatController] Execution Time: ${executionTime}ms`);

      // ALWAYS record the model turn — keeps conversation history balanced.
      // ALWAYS display the answer — user always gets a response.
      appendModel(answer);
      setMessages(prev => [...prev, { id: Date.now(), role: 'bot', text: answer, options: msgOptions }]);
      setLoading(false);
    }
  }, [loading]);

  const clearChat = useCallback(() => {
    resetHistory();
    setMessages([WELCOME_MESSAGE]);
  }, []);

  return { messages, loading, send, clearChat };
}
